import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-f4eff70c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "virus-distribution-chart-wrap" }
const _hoisted_2 = { class: "chart-container" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignPieOption } from '@/utils/echarts-option/pie'
import Card from '@/views/dashboard/_components/card/card.vue'
import PieCenter from '@/views/dashboard/_components/echarts/pie-center.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import TimeRangeSelect from '@/views/dashboard/_components/time-range-select/time-range-select.vue'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { getTimeRangeByEnum } from '@/utils/time'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import {
  VirusDistributionChartService,
  VirusDistributionRes,
} from './virus-distribution-chart.service'
import { getThreatTagTitle } from '@/services/threat-tag.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'virus-distribution-chart',
  emits: ["sliceClick"],
  setup(__props, { emit: __emit }) {

const injector = useReflectiveInjector([VirusDistributionChartService, ResizeService])
const service = injector.get(VirusDistributionChartService)
const resizeService = injector.get(ResizeService)
const { t, locale } = useI18n()

const emit = __emit

const riskTypeLabelI18n = computed(() => ({
  1: t('safeProtectionModule.riskCatWebSettings'),
  2: t('safeProtectionModule.riskCatSystemGeneral'),
  3: t('safeProtectionModule.riskCatSystemSecurity'),
  4: t('safeProtectionModule.riskCatBrowser'),
  5: t('safeProtectionModule.riskCatMobile'),
  6: t('safeProtectionModule.riskCatExtDir'),
  10000: t('safeProtectionModule.riskCatCustomDir'),
  20000: t('safeProtectionModule.fileSecurity'),
  1000000: t('safeProtectionModule.activeDefense'),
}))

const isLoading = ref(false)
const timeRange = ref<TimeRange>(TimeRange.DAYS_30)
const timeRangeValue = ref<[number, number]>(getTimeRangeByEnum(TimeRange.DAYS_30))
const rawData = ref<VirusDistributionRes>({
  threat_types: [],
  counts: [],
})

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

/**
 * 图表数据：distribution 下发 threat_types 为威胁 slug（如 spyware）→ threatTag.slug.*.title；
 * 纯数字串仍走旧版风险大类 riskTypeLabelI18n（与风险列表筛选项一致）。
 */
const chartData = computed(() => {
  void locale.value
  if (!rawData.value?.threat_types?.length || !rawData.value?.counts) return []
  const legacyMap = riskTypeLabelI18n.value as Record<number, string>
  return rawData.value.threat_types.map((type, index) => {
    const tag = String(type).trim()
    const slugTitle = getThreatTagTitle(tag, t)
    const n = Number(tag)
    const legacyName =
      /^\d+$/.test(tag) && Number.isFinite(n) && legacyMap[n] ? legacyMap[n] : undefined
    const name =
      slugTitle
      ?? legacyName
      ?? (tag.toLowerCase() === 'unknown' ? t('ransomwareIncident.unknown') : tag)
    const risk_type = /^\d+$/.test(tag) && Number.isFinite(n) ? n : undefined
    return {
      name,
      value: rawData.value.counts[index] || 0,
      threat_tag: tag,
      risk_type,
    }
  })
})

const totalCount = computed(() =>
  chartData.value.reduce((sum, item) => sum + item.value, 0)
)

const hasData = computed(() => totalCount.value > 0)

function getChartData () {
  return chartData.value.map((item) => ({
    value: item.value,
    name: item.name,
    risk_type: item.risk_type,
    threat_tag: item.threat_tag,
  }))
}

function bindChartClick () {
  chartInstance?.off('click')
  chartInstance?.on('click', (params: unknown) => {
    const p = params as {
      componentType?: string
      data?: {
        name?: string
        value?: number
        realValue?: number
        risk_type?: number
        threat_tag?: string
      }
    }
    if (p.componentType !== 'series' || !p.data) return
    const [start_time, end_time] = timeRangeValue.value
    const d = p.data
    if (d.risk_type != null && Number.isFinite(Number(d.risk_type))) {
      emit('sliceClick', {
        risk_type: Number(d.risk_type),
        start_time,
        end_time,
      })
      return
    }
    const slug = d.threat_tag != null ? String(d.threat_tag).trim() : ''
    if (!slug) return
    emit('sliceClick', {
      threat_tag: slug,
      start_time,
      end_time,
    })
  })
}

function createChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  bindChartClick()
}

function getChartOption () {
  const option = {
    series: [{
      name: t('safeProtectionModule.chartQuantity'),
      data: getChartData(),
      cursor: 'pointer',
    }],
  }

  return getAssignPieOption(option, { unit: t('safeProtectionModule.countUnit') })
}

function renderChart () {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption())
  bindChartClick()
}

function initChart () {
  chartInstance?.dispose()
  createChart()
  renderChart()
}

function handleTimeRangeChange (value: TimeRange, timeRangeArray: [number, number]) {
  timeRange.value = value
  timeRangeValue.value = timeRangeArray
  loadData()
}

function loadData () {
  isLoading.value = true
  const [start_time, end_time] = timeRangeValue.value

  service.getVirusDistribution({ start_time, end_time }).then((res) => {
    rawData.value = res
  }).finally(() => {
    isLoading.value = false
  })
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance?.resize()
})

watch(chartData, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

useEchartsThemeSync(renderChart)

onMounted(() => {
  loadData()
  if (hasData.value) {
    setTimeout(() => initChart(), 100)
  }
})

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance?.dispose()
})

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('safeProtectionModule.virusTypeDist'),
    class: "chart-card"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            ref_key: "chartRef",
            ref: chartRef,
            class: "chart"
          }, null, 512),
          _createVNode(PieCenter, {
            title: _ctx.$t('examinationTable.pieCenterTotal'),
            value: totalCount.value,
            unit: _ctx.$t('safeProtectionModule.countUnit')
          }, null, 8, ["title", "value", "unit"])
        ], 512), [
          [_vShow, hasData.value]
        ]),
        _withDirectives(_createVNode(EmptyData, {
          height: "100%",
          "bg-color": "transparent"
        }, null, 512), [
          [_vShow, !hasData.value]
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})